import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  
  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'portfolio', name: 'Portfolio' },
    { id: 'business', name: 'Business' },
    { id: 'ecommerce', name: 'E-commerce' },
    { id: 'education', name: 'Educational' },
    { id: 'landing', name: 'Landing Pages' }
  ];
  
  const projects = [
    {
      id: 1,
      title: "NeoTech Solutions",
      category: "business",
      image: "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "Corporate website for a leading tech company with interactive features and custom animations.",
      client: "NeoTech Inc.",
      services: ["Web Design", "Development", "SEO"],
      link: "#"
    },
    {
      id: 2,
      title: "Fusion Fashion",
      category: "ecommerce",
      image: "https://images.unsplash.com/photo-1525507119028-ed4c629a60a3?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "Full-featured e-commerce platform with product filtering, secure checkout, and inventory management.",
      client: "Fusion Style Group",
      services: ["E-commerce Development", "Payment Integration", "Mobile Optimization"],
      link: "#"
    },
    {
      id: 3,
      title: "Alex Porter Photography",
      category: "portfolio",
      image: "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "Minimalist portfolio website showcasing professional photography with smooth gallery transitions.",
      client: "Alex Porter",
      services: ["Portfolio Design", "Gallery Implementation", "SEO"],
      link: "#"
    },
    {
      id: 4,
      title: "EduLearn Platform",
      category: "education",
      image: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "Interactive educational platform with course management, student tracking, and video lessons.",
      client: "EduLearn Foundation",
      services: ["LMS Development", "User Authentication", "Content Management"],
      link: "#"
    },
    {
      id: 5,
      title: "Quantum VR",
      category: "landing",
      image: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "High-converting landing page for a VR product launch with interactive 3D elements.",
      client: "Quantum Tech",
      services: ["Landing Page Design", "3D Integration", "Conversion Optimization"],
      link: "#"
    },
    {
      id: 6,
      title: "Organic Harvest",
      category: "ecommerce",
      image: "https://images.unsplash.com/photo-1550989460-0adf9ea622e2?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "E-commerce website for organic products with subscription options and customer loyalty program.",
      client: "Organic Harvest Co.",
      services: ["E-commerce Development", "Subscription System", "Mobile App"],
      link: "#"
    },
    {
      id: 7,
      title: "Digital Agency X",
      category: "business",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "Creative agency website with parallax effects, case studies, and team portfolio.",
      client: "Agency X Partners",
      services: ["Web Design", "Animation", "CMS Implementation"],
      link: "#"
    },
    {
      id: 8,
      title: "CodeCamp",
      category: "education",
      image: "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&h=800&q=80",
      description: "Coding bootcamp platform with curriculum management and student progress tracking.",
      client: "CodeCamp Institute",
      services: ["Learning Platform", "Progress Tracking", "Payment System"],
      link: "#"
    }
  ];
  
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);
  
  return (
    <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-orbitron font-bold mb-6">
            Our <span className="text-cyber-purple">Portfolio</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Take a look at our recent projects. Each website is custom-designed to meet our clients' unique needs and goals.
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setFilter(category.id)}
              className={`px-5 py-2 rounded-full font-medium transition-all duration-300 ${
                filter === category.id
                  ? 'bg-cyber-pink text-white'
                  : 'bg-cyber-gray/50 text-gray-300 hover:bg-cyber-gray'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
              className="cyber-card overflow-hidden cursor-pointer"
              onClick={() => setSelectedProject(project)}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute inset-0 bg-cyber-dark/60 opacity-0 hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <h3 className="text-2xl font-orbitron font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-cyber-neon uppercase text-sm tracking-wider">{categories.find(c => c.id === project.category)?.name}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Project Detail Modal */}
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-cyber-dark/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-cyber-gray max-w-4xl w-full rounded-lg shadow-xl overflow-hidden relative"
            >
              <button 
                className="absolute top-4 right-4 text-white hover:text-cyber-pink z-10 bg-cyber-dark/50 w-10 h-10 rounded-full flex items-center justify-center"
                onClick={() => setSelectedProject(null)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="h-64 md:h-96 relative">
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyber-gray via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="inline-block px-3 py-1 bg-cyber-pink/80 text-white text-sm rounded-full mb-3">
                    {categories.find(c => c.id === selectedProject.category)?.name}
                  </div>
                  <h3 className="text-3xl font-orbitron font-bold text-white">{selectedProject.title}</h3>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <h4 className="text-xl font-orbitron mb-4 text-cyber-neon">Project Overview</h4>
                    <p className="text-gray-300 mb-6">{selectedProject.description}</p>
                    
                    <a 
                      href={selectedProject.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cyber-button"
                    >
                      <span>Visit Website</span>
                    </a>
                  </div>
                  
                  <div className="border-l border-cyber-purple/30 pl-6">
                    <div className="mb-6">
                      <h4 className="text-lg font-orbitron mb-2 text-white">Client</h4>
                      <p className="text-gray-300">{selectedProject.client}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-orbitron mb-2 text-white">Services</h4>
                      <ul className="space-y-1">
                        {selectedProject.services.map((service, index) => (
                          <li key={index} className="text-gray-300">
                            <span className="text-cyber-pink mr-2">›</span> {service}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio; 